'use client';

import React, { useState, useEffect } from 'react';
import {
  calculateZodiacCompatibility,
  getZodiacCompatibilityMatrix,
  ZodiacPairCompatibilityResult,
  ZodiacMatrixGridResult
} from '@/lib/api/client';
import { ZODIAC_METADATA } from '@/lib/config/zodiac';
import { Heart, MessageSquare, Users, Flame, Grid } from 'lucide-react';
import { CalculatorPageContent } from '@/components/calculators/CalculatorPageContent';

export default function ZodiacCompatibilityPage() {
  const [signA, setSignA] = useState('aries');
  const [signB, setSignB] = useState('leo');
  const [result, setResult] = useState<ZodiacPairCompatibilityResult | null>(null);
  const [matrix, setMatrix] = useState<ZodiacMatrixGridResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch initial matrix and default pair result
  useEffect(() => {
    fetchPair('aries', 'leo');
    getZodiacCompatibilityMatrix()
      .then((m) => setMatrix(m))
      .catch(() => {});
  }, []);

  const fetchPair = async (a: string, b: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateZodiacCompatibility(a, b);
      setResult(data);
      setSignA(a);
      setSignB(b);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch Zodiac compatibility');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchPair(signA, signB);
  };

  const metaA = ZODIAC_METADATA.find((z) => z.sign === signA) || ZODIAC_METADATA[0];
  const metaB = ZODIAC_METADATA.find((z) => z.sign === signB) || ZODIAC_METADATA[4];

  return (
    <div className="space-y-10 max-w-5xl mx-auto">
      {/* Header Banner */}
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl relative overflow-hidden text-center sm:text-left">
        <div className="flex items-center justify-center sm:justify-start gap-2 text-[#5ab4ff] text-[11px] font-bold uppercase tracking-wider mb-2">
          <Heart className="w-4 h-4 text-[#5ab4ff]" />
          <span>Western Sun-Sign Matcher</span>
          <span className="px-2.5 py-0.5 rounded-full bg-white/10 text-[#b9c6ff] border border-white/15 text-[10px]">Casual &amp; Fun</span>
        </div>

        <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-white mb-2">
          Zodiac Sign Love Compatibility
        </h1>
        <p className="text-[rgba(220,236,255,0.85)] text-xs sm:text-sm max-w-2xl leading-[1.6]">
          Discover quick Western tropical Sun-sign compatibility across Love, Communication, Friendship, and Sexual chemistry based on astrological elements and planetary rulers!
        </p>
      </div>

      {/* Input Selector Form */}
      <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label htmlFor="zodiac-sign-a" className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">
                First Zodiac Sign
              </label>
              <select
                id="zodiac-sign-a"
                value={signA}
                onChange={(e) => setSignA(e.target.value)}
                className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] focus:outline-none cursor-pointer [color-scheme:dark]"
              >
                {ZODIAC_METADATA.map((z) => (
                  <option key={z.sign} value={z.sign} className="bg-[#050a24] text-white">
                    {z.symbol} {z.name} ({z.element} • {z.dates})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="zodiac-sign-b" className="text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-1.5 block">
                Second Zodiac Sign
              </label>
              <select
                id="zodiac-sign-b"
                value={signB}
                onChange={(e) => setSignB(e.target.value)}
                className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:ring-2 focus:ring-[#5ab4ff] focus:border-[#5ab4ff] focus:outline-none cursor-pointer [color-scheme:dark]"
              >
                {ZODIAC_METADATA.map((z) => (
                  <option key={z.sign} value={z.sign} className="bg-[#050a24] text-white">
                    {z.symbol} {z.name} ({z.element} • {z.dates})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-white/90 shadow-[0_0_25px_rgba(150,165,255,0.4)] disabled:opacity-50 transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98]"
          >
            {loading ? 'Analyzing Zodiac Match...' : `Check ${metaA.name} & ${metaB.name} Compatibility`}
          </button>
        </form>
      </div>

      {error && (
        <div className="bg-rose-500/10 border border-[#ff9bb0]/30 rounded-xl p-4 text-[#ff9bb0] text-xs">
          {error}
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div className="space-y-6">
          {/* Main Score Banner */}
          <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 sm:p-8 shadow-xl text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6 relative overflow-hidden">
            <div className="flex items-center gap-4">
              <div className="text-3xl bg-white/10 p-4 rounded-2xl border border-white/20 flex items-center gap-2 text-white">
                <span>{metaA.symbol}</span>
                <Heart className="w-5 h-5 text-[#ff9bb0]" />
                <span>{metaB.symbol}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold text-[#5ab4ff] uppercase tracking-wider block">
                  Overall Compatibility
                </span>
                <h2 id="zodiac-result-title" className="text-2xl font-heading font-semibold text-white">
                  {metaA.name} &amp; {metaB.name}
                </h2>
                <span className="text-xs text-[rgba(220,236,255,0.75)]">
                  {metaA.element} Sign × {metaB.element} Sign
                </span>
              </div>
            </div>

            <div className="bg-white/[0.04] border border-white/12 rounded-xl px-6 py-4 text-center">
              <div className="text-3xl font-bold font-mono text-[#b9c6ff]">
                {result.overallLove}%
              </div>
              <div className="text-[10px] uppercase font-bold text-[rgba(220,236,255,0.7)] tracking-wider mt-0.5">
                Match Score
              </div>
            </div>
          </div>

          {/* 4 Detailed Category Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Love */}
            <div className="rounded-2xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-5 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white font-heading font-semibold text-xs">
                  <Heart className="w-4 h-4 text-[#ff9bb0]" />
                  <span>Love &amp; Romance</span>
                </div>
                <span className="font-mono text-xs font-bold text-[#b9c6ff]">{result.categories.love.percent}%</span>
              </div>
              <p className="text-xs text-[rgba(220,236,255,0.85)] leading-[1.6]">
                {result.categories.love.blurb}
              </p>
            </div>

            {/* Communication */}
            <div className="rounded-2xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-5 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white font-heading font-semibold text-xs">
                  <MessageSquare className="w-4 h-4 text-[#5ab4ff]" />
                  <span>Communication &amp; Intellect</span>
                </div>
                <span className="font-mono text-xs font-bold text-[#5ab4ff]">{result.categories.communication.percent}%</span>
              </div>
              <p className="text-xs text-[rgba(220,236,255,0.85)] leading-[1.6]">
                {result.categories.communication.blurb}
              </p>
            </div>

            {/* Friendship */}
            <div className="rounded-2xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-5 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white font-heading font-semibold text-xs">
                  <Users className="w-4 h-4 text-[#8dffc9]" />
                  <span>Friendship &amp; Loyalty</span>
                </div>
                <span className="font-mono text-xs font-bold text-[#8dffc9]">{result.categories.friendship.percent}%</span>
              </div>
              <p className="text-xs text-[rgba(220,236,255,0.85)] leading-[1.6]">
                {result.categories.friendship.blurb}
              </p>
            </div>

            {/* Sexual */}
            <div className="rounded-2xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-5 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-white font-heading font-semibold text-xs">
                  <Flame className="w-4 h-4 text-[#ffd36b]" />
                  <span>Physical &amp; Chemistry</span>
                </div>
                <span className="font-mono text-xs font-bold text-[#ffd36b]">{result.categories.sexual.percent}%</span>
              </div>
              <p className="text-xs text-[rgba(220,236,255,0.85)] leading-[1.6]">
                {result.categories.sexual.blurb}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 12x12 Summary Matrix Grid */}
      {matrix && (
        <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-b from-white/[0.08] to-white/[0.03] bg-[rgba(5,10,36,0.92)] border border-white/16 backdrop-blur-xl p-6 shadow-xl space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-heading font-medium text-white flex items-center gap-2">
              <Grid className="w-5 h-5 text-[#5ab4ff]" />
              <span>Full 12×12 Zodiac Compatibility Grid</span>
            </h3>
            <span className="text-xs text-[rgba(220,236,255,0.75)]">Click any cell to inspect detailed pair score</span>
          </div>

          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table id="zodiac-matrix-table" className="w-full text-center text-xs border-collapse">
              <thead>
                <tr>
                  <th className="p-2 border border-white/10 bg-white/10 text-[rgba(220,236,255,0.75)] font-mono">Sign</th>
                  {ZODIAC_METADATA.map((z) => (
                    <th key={z.sign} className="p-2 border border-white/10 bg-white/10 text-white font-semibold">
                      {z.symbol}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ZODIAC_METADATA.map((row) => (
                  <tr key={row.sign}>
                    <td className="p-2 border border-white/10 bg-white/[0.04] text-white font-semibold text-left">
                      {row.symbol} {row.name}
                    </td>
                    {ZODIAC_METADATA.map((col) => {
                      const score = matrix[row.sign]?.[col.sign] || 50;
                      const isSelected = (signA === row.sign && signB === col.sign) || (signA === col.sign && signB === row.sign);
                      return (
                        <td
                          key={col.sign}
                          onClick={() => fetchPair(row.sign, col.sign)}
                          className={`p-2 border border-white/10 cursor-pointer font-mono font-bold transition-all ${
                            isSelected
                              ? 'bg-white text-[#03163d] ring-2 ring-[#5ab4ff]'
                              : score >= 90
                              ? 'bg-[#5ab4ff]/25 text-white hover:bg-[#5ab4ff]/40'
                              : score >= 75
                              ? 'bg-[#5ab4ff]/10 text-[#b9c6ff] hover:bg-[#5ab4ff]/20'
                              : 'bg-white/[0.02] text-[rgba(220,236,255,0.75)] hover:bg-white/10'
                          }`}
                        >
                          {score}%
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Differentiation Note */}
      <div className="rounded-xl bg-white/[0.04] border border-white/12 p-4 text-xs text-[rgba(220,236,255,0.85)] italic text-center">
        Looking for authentic Vedic astronomical matching for marriage decisions? Explore our{' '}
        <a href="/kundli-matching" className="text-[#5ab4ff] hover:underline font-semibold">
          36-Point Ashtakoot Kundli Matching
        </a>{' '}
        powered by Swiss Ephemeris.
      </div>

      <CalculatorPageContent
        calculatorName="Zodiac Compatibility Calculator"
        introParagraph="Zodiac Compatibility compares pair harmony across Western Sun signs based on elemental triplicities (Fire, Earth, Air, Water) and quadrupeds (Cardinal, Fixed, Mutable)."
        methodologyTitle="How Western Zodiac Compatibility Works"
        methodologyContent="I calculate Western Zodiac compatibility by evaluating planetary ruler harmony and elemental synergy between two Sun sign dates."
        subsections={[
          {
            title: "Elemental Triplicities",
            content: "Fire (Aries, Leo, Sagittarius), Earth (Taurus, Virgo, Capricorn), Air (Gemini, Libra, Aquarius), and Water (Cancer, Scorpio, Pisces) define elemental baseline attraction."
          },
          {
            title: "Sun Sign vs Vedic Kundli Matching",
            content: "Western Sun sign compatibility provides a fun, general personality overview. For serious marital decisions, Vedic 36-Point Ashtakoot Guna Milan evaluates exact birth Moon positions."
          }
        ]}
        faqs={[
          {
            q: "What is Western Zodiac Compatibility?",
            a: "It is a personality-focused compatibility system based on Western Sun sign elements and planetary rulers."
          },
          {
            q: "Is Sun sign compatibility enough for marriage decisions?",
            a: "We recommend using Vedic Kundli Matching (36 Guna Milan) for serious relationship decisions, as it incorporates exact birth time and lunar positions."
          }
        ]}
        relatedCalculators={[
          {
            title: "Kundli Matching (36 Guna)",
            href: "/kundli-matching",
            description: "Serious Vedic Ashtakoot Guna Milan for marriage compatibility.",
            categoryBadge: "Vedic"
          },
          {
            title: "Marriage Compatibility Calculator",
            href: "/marriage-compatibility-calculator",
            description: "Dedicated marriage-framed entrance to 36-point Guna Milan.",
            categoryBadge: "Marriage"
          },
          {
            title: "Western Zodiac Calculator",
            href: "/western-zodiac-calculator",
            description: "Calculate individual Western Sun sign and elemental breakdown.",
            categoryBadge: "Western"
          }
        ]}
        showSignPeriodGrid={false}
      />
    </div>
  );
}
